import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, renderSlot as _renderSlot, resolveDirective as _resolveDirective, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["disabled", "onClick"]
const _hoisted_2 = { class: "k-date__main column no-wrap gap-md" }
const _hoisted_3 = { class: "relative-position" }
const _hoisted_4 = {
  key: "years",
  class: "row no-wrap items-center full-width"
}
const _hoisted_5 = ["disabled", "aria-label"]
const _hoisted_6 = { class: "col relative-position overflow-hidden flex flex-center" }
const _hoisted_7 = { class: "col relative-position overflow-hidden flex flex-center" }
const _hoisted_8 = ["disabled", "aria-label"]
const _hoisted_9 = {
  key: "calendar",
  class: "row no-wrap items-center full-width"
}
const _hoisted_10 = ["disabled", "aria-label", "onClick"]
const _hoisted_11 = ["disabled", "onClick"]
const _hoisted_12 = ["disabled", "aria-label", "onClick"]
const _hoisted_13 = { class: "relative-position column no-wrap" }
const _hoisted_14 = {
  key: "months",
  class: "k-date__grid full-width"
}
const _hoisted_15 = ["disabled", "onClick"]
const _hoisted_16 = {
  key: "years",
  class: "k-date__grid full-width"
}
const _hoisted_17 = ["disabled", "onClick"]
const _hoisted_18 = {
  key: 1,
  class: "column no-wrap gap-sm"
}
const _hoisted_19 = { class: "row no-wrap items-center justify-between" }
const _hoisted_20 = { class: "row no-wrap items-center gap-xs fs-11px fw-500" }
const _hoisted_21 = ["aria-label"]
const _hoisted_22 = {
  key: 0,
  class: "row no-wrap items-center gap-xs fs-11px fw-500"
}
const _hoisted_23 = { class: "q-ml-auto" }
const _hoisted_24 = { class: "row no-wrap gap-sm" }

import { computed, getCurrentInstance, nextTick, ref, useAttrs, watch } from 'vue'
import type { StyleValue } from 'vue'
import { date, useQuasar } from 'quasar'
import type { QDate, QMenu } from 'quasar'
import _ from 'lodash'

type View = 'Calendar' | 'Months' | 'Years'
type Unit = 'month' | 'year'

interface IDateRange {
  from: string
  to: string
}

type ModelValue = string | IDateRange | (string | IDateRange)[] | null

interface IDateLocale {
  days?: string[]
  daysShort?: string[]
  months?: string[]
  monthsShort?: string[]
}

interface ITextObject {
  cancel: string
  apply: string
  today: string
  thisWeek: string
  thisMonth: string
  thisYear: string
  /** Word between the two dates of a range, e.g. "10 Oct 2026 to 31 Oct 2026" */
  to: string
  /** Accessible name of the button that clears a range */
  clear: string
}

interface IProps {
  modelValue?: ModelValue
  /** Shows the Today / This Week / This Month / This Year sidebar, and turns on range selection */
  presets?: boolean
  /** Keeps picked dates as a draft until Apply is pressed; Cancel throws the draft away */
  confirm?: boolean
  text?: Partial<ITextObject>

  // q-date props KDate reads as well as forwarding. Every other q-date prop and listener passes straight through.
  range?: boolean
  multiple?: boolean
  mask?: string
  locale?: IDateLocale
  calendar?: 'gregorian' | 'persian'
  firstDayOfWeek?: string | number
  defaultYearMonth?: string
  defaultView?: View
  navigationMinYearMonth?: string
  navigationMaxYearMonth?: string
  emitImmediately?: boolean
  noUnset?: boolean
  /** Always shows the Today link, not only while another month is on screen */
  todayBtn?: boolean
  readonly?: boolean
  disable?: boolean
  flat?: boolean
  bordered?: boolean
  square?: boolean
}

interface IEmits {
  (event: 'update:modelValue', value: ModelValue, reason?: string, details?: Record<string, unknown>): void
}

interface IYearMonth {
  year: number
  month: number
}

interface IDay extends IYearMonth {
  day: number
}

interface IPreset {
  key: string
  label: string
  value: ModelValue
}

interface ISummaryPart {
  text: string
  muted?: boolean
}

// Arrow labels q-date reads from $q.lang.date: every Quasar language pack has them, but Quasar's types leave them out
interface INavLabels {
  prevMonth: string
  nextMonth: string
  prevYear: string
  nextYear: string
  prevRangeYears: (range: number) => string
  nextRangeYears: (range: number) => string
}

const YEARS_PER_PAGE = 12
const SUMMARY_FORMAT = 'D MMM YYYY'
const DEFAULT_MASK = 'YYYY/MM/DD'

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KDate', inheritAttrs: false },
  __name: 'KDate',
  props: {
    modelValue: { default: null },
    presets: { type: Boolean },
    confirm: { type: Boolean },
    text: { default: () => ({}) },
    range: { type: Boolean },
    multiple: { type: Boolean },
    mask: { default: undefined },
    locale: { default: undefined },
    calendar: { default: 'gregorian' },
    firstDayOfWeek: { default: 1 },
    defaultYearMonth: { default: undefined },
    defaultView: { default: 'Calendar' },
    navigationMinYearMonth: { default: undefined },
    navigationMaxYearMonth: { default: undefined },
    emitImmediately: { type: Boolean },
    noUnset: { type: Boolean },
    todayBtn: { type: Boolean },
    readonly: { type: Boolean },
    disable: { type: Boolean },
    flat: { type: Boolean },
    bordered: { type: Boolean },
    square: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const VIEWS: View[] = ['Calendar', 'Months', 'Years']
const DEFAULT_TEXT: ITextObject = {
  cancel: 'Cancel',
  apply: 'Apply',
  today: 'Today',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  thisYear: 'This Year',
  to: 'to',
  clear: 'Clear',
}



const props = __props

const emit = __emit

const $q = useQuasar()
const attrs = useAttrs()

const qDateRef = ref<QDate | null>(null)

const today = new Date()

const labels = computed(() => ({ ...DEFAULT_TEXT, ...props.text }))
const navLabels = computed(() => $q.lang.date as typeof $q.lang.date & INavLabels)
const dateLocale = computed(() => ({ ...$q.lang.date, ...props.locale }))
const dateMask = computed(() => props.mask ?? DEFAULT_MASK)
const isRange = computed(() => props.range || props.presets)
const editable = computed(() => !props.disable && !props.readonly)
// Phones have no room for the presets beside the calendar, so they go above it
const isStacked = computed(() => props.presets && $q.screen.lt.sm)
// Same condition q-date uses before it emits on navigation
const isImmediate = computed(() => props.emitImmediately && !props.multiple && !isRange.value)

/* ---------------------------------- Model --------------------------------- */

// With `confirm`, q-date edits this draft and the model only changes on Apply
const draft = ref<ModelValue>(_.cloneDeep(props.modelValue))
// Reason and details of the last change q-date made to the draft, re-emitted on Apply
let draftChange: { reason?: string; details?: Record<string, unknown> } = {}
// Value the picker opened with, which Cancel restores when every click is saved straight away
const openingValue = _.cloneDeep(props.modelValue)

const value = computed(() => (props.confirm ? draft.value : props.modelValue))

function commit(newValue: ModelValue, reason?: string, details?: Record<string, unknown>) {
  editingFrom.value = null

  if (!props.confirm) return emit('update:modelValue', newValue, reason, details)
  draft.value = newValue
  draftChange = { reason, details }
}

function apply() {
  if (props.confirm && !_.isEqual(draft.value, props.modelValue))
    emit('update:modelValue', draft.value, draftChange.reason, draftChange.details)
}

function cancel() {
  qDateRef.value?.setEditingRange()
  editingFrom.value = null

  if (props.confirm) draft.value = _.cloneDeep(props.modelValue)
  else if (!_.isEqual(props.modelValue, openingValue)) emit('update:modelValue', _.cloneDeep(openingValue))
}

function clear() {
  qDateRef.value?.setEditingRange()
  commit(null)
}

const canClear = computed(() => editable.value && !props.noUnset)

const isSelected = (candidate: ModelValue) => _.isEqual(candidate, value.value)

/* ---------------------------------- Dates --------------------------------- */

function parseYearMonth(text?: string): IYearMonth | null {
  const match = /^(-?\d+)\/(\d{2})$/.exec(text ?? '')
  return match ? { year: Number(match[1]), month: Number(match[2]) } : null
}

const toIndex = ({ year, month }: IYearMonth) => year * 12 + month - 1
const fromIndex = (index: number): IYearMonth => ({ year: Math.floor(index / 12), month: (index % 12) + 1 })

function decode(text: string) {
  const parsed = date.extractDate(text, dateMask.value, dateLocale.value)
  // extractDate fills gaps instead of failing, so only trust text that formats back to itself
  return date.formatDate(parsed, dateMask.value, dateLocale.value) === text ? parsed : null
}

const encode = (day: Date) => date.formatDate(day, dateMask.value, dateLocale.value)

function modelDays(model: ModelValue) {
  const entries = Array.isArray(model) ? model : model ? [model] : []

  return entries
    .flatMap((entry) => (typeof entry === 'string' ? [entry] : [entry.from, entry.to]))
    .map(decode)
    .filter((day): day is Date => day !== null)
    .sort((a, b) => a.getTime() - b.getTime())
}

/* ------------------------------- Navigation ------------------------------- */

// Month on screen, kept in step with q-date through its `navigation` event
const displayed = ref<IYearMonth>(getInitialView())
const view = ref<View>(VIEWS.includes(props.defaultView) ? props.defaultView : 'Calendar')
// First year of the 12 shown in the Years view
const yearsStart = ref(decadeStart(displayed.value.year))

const minNav = computed(() => parseYearMonth(props.navigationMinYearMonth))
const maxNav = computed(() => parseYearMonth(props.navigationMaxYearMonth))

// Same month q-date opens on: the last date in the model, then default-year-month, then today
function getInitialView(): IYearMonth {
  const entries = Array.isArray(value.value) ? value.value : value.value ? [value.value] : []
  const last = entries.at(-1)
  const text = typeof last === 'object' && last !== null ? last.from : last
  const parsed = typeof text === 'string' ? decode(text) : null

  if (parsed) return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 }
  return parseYearMonth(props.defaultYearMonth) ?? { year: today.getFullYear(), month: today.getMonth() + 1 }
}

function decadeStart(year: number) {
  return year - (year % 10)
}

function isOutsideNav(target: IYearMonth) {
  return (
    (minNav.value !== null && toIndex(target) < toIndex(minNav.value)) ||
    (maxNav.value !== null && toIndex(target) > toIndex(maxNav.value))
  )
}

// Mirrors the boundaries q-date puts on its own arrows
const navigation = computed(() => {
  const { year, month } = displayed.value
  const boundaries = { month: { prev: true, next: true }, year: { prev: true, next: true } }

  if (minNav.value !== null && minNav.value.year >= year) {
    boundaries.year.prev = false
    if (minNav.value.year === year && minNav.value.month >= month) boundaries.month.prev = false
  }
  if (maxNav.value !== null && maxNav.value.year <= year) {
    boundaries.year.next = false
    if (maxNav.value.year === year && maxNav.value.month <= month) boundaries.month.next = false
  }

  return [
    {
      type: 'month' as Unit,
      view: 'Months' as View,
      label: dateLocale.value.months[month - 1],
      prev: editable.value && boundaries.month.prev,
      next: editable.value && boundaries.month.next,
      prevLabel: navLabels.value.prevMonth,
      nextLabel: navLabels.value.nextMonth,
    },
    {
      type: 'year' as Unit,
      view: 'Years' as View,
      label: String(year),
      prev: editable.value && boundaries.year.prev,
      next: editable.value && boundaries.year.next,
      prevLabel: navLabels.value.prevYear,
      nextLabel: navLabels.value.nextYear,
    },
  ]
})

const pageYears = computed(() => Array.from({ length: YEARS_PER_PAGE }, (_value, index) => yearsStart.value + index))

const yearsPaging = computed(() => ({
  prev: editable.value && (minNav.value === null || minNav.value.year < yearsStart.value),
  next: editable.value && (maxNav.value === null || maxNav.value.year > yearsStart.value + YEARS_PER_PAGE - 1),
}))

const isMonthDisabled = (month: number) => isOutsideNav({ year: displayed.value.year, month })
const isYearDisabled = (year: number) =>
  (minNav.value !== null && minNav.value.year > year) || (maxNav.value !== null && maxNav.value.year < year)

const arrowIcon = (enabled: boolean) => ({ color: enabled ? 'cool-gray-70' : 'cool-gray-50', hoverable: enabled })

// Way the header labels jump when they change: later dates come in from the right, earlier ones from the left, as in q-date
const jump = ref<'left' | 'right'>('left')
const jumpTransition = computed(() => `q-transition--jump-${jump.value}`)

function onNavigation({ year, month }: IYearMonth) {
  jump.value = toIndex({ year, month }) > toIndex(displayed.value) ? 'left' : 'right'
  displayed.value = { year, month }
}

function turnYearsPage(step: 1 | -1) {
  jump.value = step > 0 ? 'left' : 'right'
  yearsStart.value += step * YEARS_PER_PAGE
}

function setView(next: View) {
  if (!VIEWS.includes(next)) return
  if (next === 'Years') yearsStart.value = decadeStart(displayed.value.year)
  view.value = next
}

function toggleView(target: View) {
  setView(view.value === target ? 'Calendar' : target)
}

function offsetCalendar(type: Unit, descending?: boolean) {
  qDateRef.value?.offsetCalendar(type, descending)
}

function setCalendarTo(year?: number, month?: number) {
  qDateRef.value?.setCalendarTo(year, month)
  view.value = 'Calendar'
}

/**
 * Moves the calendar like picking from q-date's own month / year grids,
 * including the model update those grids make when `emit-immediately` is on
 */
async function moveCalendar(target: IYearMonth, unit: Unit) {
  const qDate = qDateRef.value
  if (!qDate) return
  if (!isImmediate.value) return qDate.setCalendarTo(target.year, target.month)

  // q-date only emits on navigation from its own arrows, so land one step away and take the last step through offsetCalendar
  const step = (direction: number) =>
    unit === 'month' ? fromIndex(toIndex(target) + direction) : { year: target.year + direction, month: target.month }
  const start = [
    { from: step(-1), descending: false },
    { from: step(1), descending: true },
  ].find(({ from }) => !isOutsideNav(from))

  if (!start) return qDate.setCalendarTo(target.year, target.month)

  qDate.setCalendarTo(start.from.year, start.from.month)
  await nextTick()
  qDate.offsetCalendar(unit, start.descending)
}

function pickMonth(month: number) {
  moveCalendar({ year: displayed.value.year, month }, 'month')
  view.value = 'Calendar'
}

function pickYear(year: number) {
  moveCalendar({ year, month: displayed.value.month }, 'year')
  view.value = props.defaultView === 'Years' ? 'Months' : 'Calendar'
}

/* ---------------------------------- Today --------------------------------- */

const showToday = computed(
  () =>
    editable.value &&
    (props.todayBtn || displayed.value.year !== today.getFullYear() || displayed.value.month !== today.getMonth() + 1),
)

function setToday() {
  qDateRef.value?.setToday()
  view.value = 'Calendar'
}

/* --------------------------------- Presets -------------------------------- */

const presetList = computed<IPreset[]>(() => {
  const year = today.getFullYear()
  const month = today.getMonth()
  const day = today.getDate()
  const weekStart = day - ((today.getDay() - Number(props.firstDayOfWeek) + 7) % 7)
  const span = (from: Date, to: Date) => ({ from: encode(from), to: encode(to) })

  return [
    { key: 'today', label: labels.value.today, value: encode(new Date(year, month, day)) },
    {
      key: 'week',
      label: labels.value.thisWeek,
      value: span(new Date(year, month, weekStart), new Date(year, month, weekStart + 6)),
    },
    {
      key: 'month',
      label: labels.value.thisMonth,
      value: span(new Date(year, month, 1), new Date(year, month + 1, 0)),
    },
    { key: 'year', label: labels.value.thisYear, value: span(new Date(year, 0, 1), new Date(year, 11, 31)) },
  ]
})

function applyPreset(preset: ModelValue, event: MouseEvent) {
  qDateRef.value?.setEditingRange()
  commit(_.cloneDeep(preset))
  view.value = 'Calendar'

  // A preset half hidden at the edge of the phone strip scrolls fully into view once picked
  const button = event.currentTarget as HTMLElement
  if (isStacked.value) button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
}

/* --------------------------------- Summary -------------------------------- */

// Start of a range that has been picked but not finished yet
const editingFrom = ref<IDay | null>(null)

function onRangeStart(from: IDay) {
  editingFrom.value = from
}

function onRangeEnd() {
  editingFrom.value = null
}

const summary = computed<ISummaryPart[]>(() => {
  if (!isRange.value) return []

  const format = (day: Date) => date.formatDate(day, SUMMARY_FORMAT, dateLocale.value)

  if (editingFrom.value) {
    const { year, month, day } = editingFrom.value
    return [{ text: format(new Date(year, month - 1, day)) }, { text: labels.value.to, muted: true }, { text: '…' }]
  }

  const days = modelDays(value.value)
  if (!days.length) return []

  const first = days[0]
  const last = days[days.length - 1]
  if (date.isSameDate(first, last, 'day')) return [{ text: format(first) }]
  return [{ text: format(first) }, { text: labels.value.to, muted: true }, { text: format(last) }]
})

/* ---------------------------------- Attrs --------------------------------- */

// class and style go on KDate's card, the rest (q-date props and listeners) on q-date
function qDateAttrs() {
  return _.omit(attrs, ['class', 'style'])
}

/* ---------------------------------- Popup --------------------------------- */

// q-menu (what q-popup-proxy opens on wide screens) sizes itself to its content only when it opens. KDate grows and
// shrinks after that (the range row, months with more or fewer weeks), so it asks the menu to measure again, as q-select does.
const menu = findMenu()

function findMenu() {
  let parent = getCurrentInstance()?.parent
  while (parent && parent.type.name !== 'QMenu') parent = parent.parent
  return (parent?.proxy as QMenu | null | undefined) ?? null
}

/* -------------------------------- Watchers -------------------------------- */

// Outside changes to the model reset the draft
watch(
  () => props.modelValue,
  (model) => {
    draft.value = _.cloneDeep(model)
    draftChange = {}
  },
  { deep: true },
)

__expose({
  setToday,
  setView,
  offsetCalendar,
  setCalendarTo,
  setEditingRange(from?: Partial<IDay>, to?: Partial<IDay>) {
    qDateRef.value?.setEditingRange(from, to)
    editingFrom.value =
      isRange.value && from
        ? {
            year: from.year ?? displayed.value.year,
            month: from.month ?? displayed.value.month,
            day: from.day ?? 1,
          }
        : null
    if (from) view.value = 'Calendar'
  },
})

return (_ctx: any,_cache: any) => {
  const _component_q_resize_observer = _resolveComponent("q-resize-observer")!
  const _component_k_icon = _resolveComponent("k-icon")!
  const _component_q_date = _resolveComponent("q-date")!
  const _component_k_divider = _resolveComponent("k-divider")!
  const _component_k_btn = _resolveComponent("k-btn")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["k-date column no-wrap bg-white-shadow", [
      _ctx.$attrs.class,
      __props.presets ? 'k-date--presets rounded-24' : 'gap-md q-pa-lg rounded-32',
      {
        'shadow-2': !__props.flat,
        'no-border-radius': __props.square,
        'k-date--bordered': __props.bordered,
        'k-date--disabled': __props.disable,
        'k-date--stacked': isStacked.value,
      },
    ]]),
    style: _normalizeStyle(_ctx.$attrs.style as StyleValue)
  }, [
    (_unref(menu))
      ? (_openBlock(), _createBlock(_component_q_resize_observer, {
          key: 0,
          debounce: 0,
          onResize: _cache[0] || (_cache[0] = ($event: any) => (_unref(menu).updatePosition()))
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["no-wrap", [isStacked.value ? 'column' : 'row', __props.presets && 'gap-12 q-pt-md q-px-md q-pb-12']])
    }, [
      (__props.presets)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("div", {
              class: _normalizeClass(["k-date__presets no-wrap", isStacked.value ? 'row gap-sm overflow-auto hide-scrollbar' : 'column gap-md'])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(presetList.value, (preset) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: preset.key,
                  type: "button",
                  class: _normalizeClass(["k-date__preset transition fs-13px fw-500", { 'k-date__preset--active': isSelected(preset.value) }]),
                  disabled: !editable.value,
                  onClick: ($event: any) => (applyPreset(preset.value, $event))
                }, _toDisplayString(preset.label), 11, _hoisted_1))
              }), 128))
            ], 2),
            _cache[3] || (_cache[3] = _createElementVNode("div", { class: "k-date__separator" }, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_Transition, { name: "q-transition--fade" }, {
            default: _withCtx(() => [
              (view.value === 'Years')
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "k-date__icon-button q-px-none q-py-xs",
                      disabled: !yearsPaging.value.prev,
                      "aria-label": navLabels.value.prevRangeYears(YEARS_PER_PAGE),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (turnYearsPage(-1)))
                    }, [
                      _createVNode(_component_k_icon, _mergeProps({ name: "arrow-left" }, arrowIcon(yearsPaging.value.prev)), null, 16)
                    ], 8, _hoisted_5),
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_Transition, { name: jumpTransition.value }, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock("span", {
                            key: yearsStart.value,
                            class: "fs-15px fw-500 text-cool-gray-100"
                          }, _toDisplayString(yearsStart.value), 1))
                        ]),
                        _: 1
                      }, 8, ["name"])
                    ]),
                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "fs-15px fw-500 text-cool-gray-100" }, "-", -1)),
                    _createElementVNode("div", _hoisted_7, [
                      _createVNode(_Transition, { name: jumpTransition.value }, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock("span", {
                            key: yearsStart.value,
                            class: "fs-15px fw-500 text-cool-gray-100"
                          }, _toDisplayString(yearsStart.value + YEARS_PER_PAGE - 1), 1))
                        ]),
                        _: 1
                      }, 8, ["name"])
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "k-date__icon-button q-px-none q-py-xs",
                      disabled: !yearsPaging.value.next,
                      "aria-label": navLabels.value.nextRangeYears(YEARS_PER_PAGE),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (turnYearsPage(1)))
                    }, [
                      _createVNode(_component_k_icon, _mergeProps({ name: "arrow-right" }, arrowIcon(yearsPaging.value.next)), null, 16)
                    ], 8, _hoisted_8)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navigation.value, (unit) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: unit.type,
                        class: "col row no-wrap items-center"
                      }, [
                        (!__props.readonly)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              type: "button",
                              class: "k-date__icon-button q-px-none q-py-xs",
                              disabled: !unit.prev,
                              "aria-label": unit.prevLabel,
                              onClick: ($event: any) => (offsetCalendar(unit.type, true))
                            }, [
                              _createVNode(_component_k_icon, _mergeProps({ name: "arrow-left" }, { ref_for: true }, arrowIcon(unit.prev)), null, 16)
                            ], 8, _hoisted_10))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["k-date__title transition col relative-position overflow-hidden flex flex-center fs-15px fw-500", { 'k-date__title--active': view.value === unit.view }]),
                          disabled: !editable.value,
                          onClick: ($event: any) => (toggleView(unit.view))
                        }, [
                          _createVNode(_Transition, { name: jumpTransition.value }, {
                            default: _withCtx(() => [
                              (_openBlock(), _createElementBlock("span", {
                                key: unit.label
                              }, _toDisplayString(unit.label), 1))
                            ]),
                            _: 2
                          }, 1032, ["name"])
                        ], 10, _hoisted_11),
                        (!__props.readonly)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              type: "button",
                              class: "k-date__icon-button q-px-none q-py-xs",
                              disabled: !unit.next,
                              "aria-label": unit.nextLabel,
                              onClick: ($event: any) => (offsetCalendar(unit.type, false))
                            }, [
                              _createVNode(_component_k_icon, _mergeProps({ name: "arrow-right" }, { ref_for: true }, arrowIcon(unit.next)), null, 16)
                            ], 8, _hoisted_12))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ]))
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_Transition, { name: "q-transition--fade" }, {
            default: _withCtx(() => [
              _withDirectives(_createVNode(_component_q_date, _mergeProps({
                ref_key: "qDateRef",
                ref: qDateRef
              }, qDateAttrs(), {
                "model-value": value.value,
                range: isRange.value,
                multiple: __props.multiple,
                mask: __props.mask,
                locale: __props.locale,
                calendar: __props.calendar,
                "first-day-of-week": __props.firstDayOfWeek,
                "default-year-month": __props.defaultYearMonth,
                "navigation-min-year-month": __props.navigationMinYearMonth,
                "navigation-max-year-month": __props.navigationMaxYearMonth,
                "emit-immediately": __props.emitImmediately,
                "no-unset": __props.noUnset,
                readonly: __props.readonly,
                disable: __props.disable,
                color: "space-b-50",
                "text-color": "cool-gray-100",
                minimal: "",
                "onUpdate:modelValue": commit,
                onNavigation: onNavigation,
                onRangeStart: onRangeStart,
                onRangeEnd: onRangeEnd
              }), null, 16, ["model-value", "range", "multiple", "mask", "locale", "calendar", "first-day-of-week", "default-year-month", "navigation-min-year-month", "navigation-max-year-month", "emit-immediately", "no-unset", "readonly", "disable"]), [
                [_vShow, view.value === 'Calendar']
              ])
            ]),
            _: 1
          }),
          _createVNode(_Transition, { name: "q-transition--fade" }, {
            default: _withCtx(() => [
              (view.value === 'Months')
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dateLocale.value.months, (name, index) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: name,
                        type: "button",
                        class: _normalizeClass(["k-date__cell transition fs-13px fw-500", { 'k-date__cell--active': index + 1 === displayed.value.month }]),
                        disabled: !editable.value || isMonthDisabled(index + 1),
                        onClick: ($event: any) => (pickMonth(index + 1))
                      }, _toDisplayString(name), 11, _hoisted_15))
                    }), 128))
                  ]))
                : (view.value === 'Years')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageYears.value, (year) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: year,
                          type: "button",
                          class: _normalizeClass(["k-date__cell transition fs-13px fw-500", { 'k-date__cell--active': year === displayed.value.year }]),
                          disabled: !editable.value || isYearDisabled(year),
                          onClick: ($event: any) => (pickYear(year))
                        }, _toDisplayString(year), 11, _hoisted_17))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ])
      ])
    ], 2),
    (!__props.presets && summary.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _createVNode(_component_k_divider),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("div", _hoisted_20, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summary.value, (part, index) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: index,
                  class: _normalizeClass(part.muted ? 'text-cool-gray-70' : 'text-cool-gray-100')
                }, _toDisplayString(part.text), 3))
              }), 128))
            ]),
            (canClear.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "k-date__icon-button q-pa-none",
                  "aria-label": labels.value.clear,
                  onClick: clear
                }, [
                  _createVNode(_component_k_icon, {
                    name: "trash",
                    color: "cool-gray-70",
                    hoverable: ""
                  })
                ], 8, _hoisted_21))
              : _createCommentVNode("", true)
          ]),
          _createVNode(_component_k_divider)
        ]))
      : (_openBlock(), _createBlock(_component_k_divider, { key: 2 })),
    _createElementVNode("div", {
      class: _normalizeClass(["k-date__footer row items-center gap-sm", __props.presets && 'q-pa-md'])
    }, [
      (__props.presets)
        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summary.value, (part, index) => {
              return (_openBlock(), _createElementBlock("span", {
                key: index,
                class: _normalizeClass(part.muted ? 'text-cool-gray-70' : 'text-cool-gray-100')
              }, _toDisplayString(part.text), 3))
            }), 128))
          ]))
        : (showToday.value)
          ? (_openBlock(), _createBlock(_component_k_btn, {
              key: 1,
              class: "k-date__today",
              model: "link",
              size: "sm",
              label: labels.value.today,
              onClick: setToday
            }, null, 8, ["label"]))
          : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_23, [
        _renderSlot(_ctx.$slots, "default", {
          apply: apply,
          cancel: cancel
        }, () => [
          _createElementVNode("div", _hoisted_24, [
            _withDirectives(_createVNode(_component_k_btn, {
              class: "k-date__action",
              model: "secondary",
              size: "sm",
              label: labels.value.cancel,
              onClick: cancel
            }, null, 8, ["label"]), [
              [_directive_close_popup]
            ]),
            _withDirectives(_createVNode(_component_k_btn, {
              class: "k-date__action",
              size: "sm",
              label: labels.value.apply,
              onClick: apply
            }, null, 8, ["label"]), [
              [_directive_close_popup]
            ])
          ])
        ])
      ])
    ], 2)
  ], 6))
}
}

})